/**
 * TabPicker Application Root Component
 * 
 * Main application component that provides routing, authentication context, and
 * protected route handling for the TabPicker pull tabs odds analysis tool.
 * 
 * Features:
 * - Authentication-aware routing with protected and public routes
 * - Automatic redirects based on authentication state
 * - Loading states during authentication checks
 * - Route protection for authenticated and unauthenticated users
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './utils/sentry';
import SentryTestButton from './components/SentryTestButton';
import { DevBanner } from './components/DevBanner';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import CreateGame from './pages/CreateGame';
import EditGame from './pages/EditGame';
import ComparePage from './pages/ComparePage';
import DiscoverGames from './pages/DiscoverGames';

/**
 * Protected Route Component
 * 
 * Route wrapper that requires user authentication. Redirects unauthenticated
 * users to the landing page while showing loading state during auth checks.
 * 
 * @param props - Component props
 * @param props.children - Child components to render when authenticated
 * @returns JSX element with authentication-protected content
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-base tablet:text-xl">Loading...</div>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/" replace />;
}

/**
 * Public Route Component
 * 
 * Route wrapper for unauthenticated users. Redirects authenticated users
 * to the dashboard while showing loading state during auth checks.
 * 
 * @param props - Component props
 * @param props.children - Child components to render when unauthenticated
 * @returns JSX element with public content or redirect to dashboard
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-base tablet:text-xl">Loading...</div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

/**
 * Application Routes Component
 * 
 * Defines all application routes with appropriate authentication protection.
 * Handles route-based navigation and fallback for unknown paths.
 * 
 * @returns JSX element containing all application routes
 */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/create-game" element={
        <ProtectedRoute>
          <CreateGame />
        </ProtectedRoute>
      } />
      <Route path="/edit-game/:gameId" element={
        <ProtectedRoute>
          <EditGame />
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute>
          <ComparePage />
        </ProtectedRoute>
      } />
      <Route path="/discover" element={
        <ProtectedRoute>
          <DiscoverGames />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main Application Component
 * 
 * Root component that provides authentication context and routing
 * infrastructure for the entire TabPicker application.
 * 
 * @returns JSX element with authentication provider and router setup
 */
export default function App() {
  return (
    <ErrorBoundary fallback={({ error, resetError }) => (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h2 className="text-xl tablet:text-3xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <p className="text-base tablet:text-lg text-gray-600 mb-4">
            We're sorry, but something unexpected happened. Please try refreshing the page.
          </p>
          <div className="space-x-4">
            <button
              onClick={resetError}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    )}>
      <AuthProvider>
        <Router>
          <div className="min-h-screen flex flex-col">
            <DevBanner />
            <main className="flex-1">
              <AppRoutes />
            </main>
            <footer className="mt-auto py-4 px-6 border-t border-gray-200">
              <div className="flex flex-col tablet:flex-row justify-between items-center text-sm text-gray-600">
                <div className="mb-2 tablet:mb-0">
                  © 2025 Houfek Enterprises, LLC. All rights reserved.
                </div>
                <div className="flex space-x-4">
                  <a href="/privacy-policy.html" className="hover:text-gray-900 transition-colors">
                    Privacy Policy
                  </a>
                  <a href="/terms-of-service.html" className="hover:text-gray-900 transition-colors">
                    Terms of Service
                  </a>
                </div>
              </div>
            </footer>
            <SentryTestButton />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}
